// ABOUTME: Admin interface for managing knowledge base categories
// ABOUTME: Hierarchical tree view with drag-and-drop reordering support

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import { Add, Edit, Delete, FolderOpen } from '@mui/icons-material';
import axios from 'axios';
import { getKBCategories, createKBCategory, updateKBCategory, deleteKBCategory } from '../../../services/api';
import { KBCategoryDto, CreateKBCategoryDto, UpdateKBCategoryDto } from '../../../types/knowledgeBase';

// Maximum depth for category nesting (1 = top level, 2 = subcategory, 3 = sub-subcategory)
const MAX_CATEGORY_DEPTH = 3;

const CategoryManager: React.FC = () => {
  const [categories, setCategories] = useState<KBCategoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<KBCategoryDto | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<KBCategoryDto | null>(null);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateKBCategoryDto>({
    name: '',
    parentCategoryId: null,
    displayOrder: 0,
  });
  const [originalFormData, setOriginalFormData] = useState<CreateKBCategoryDto | null>(null);

  // Helper function to detect if form has changed from original
  const hasFormChanged = (): boolean => {
    if (!editingCategory || !originalFormData) return formData.name.trim() !== '';
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const categoriesData = await getKBCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = (parentCategory?: KBCategoryDto) => {
    setEditingCategory(null);
    setFormData({
      name: '',
      parentCategoryId: parentCategory?.id || null,
      displayOrder: 0,
    });
    setOriginalFormData(null);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (category: KBCategoryDto) => {
    setEditingCategory(category);
    const editFormData = {
      name: category.name,
      parentCategoryId: null, // Can't change parent in edit
      displayOrder: category.displayOrder,
    };
    setFormData(editFormData);
    setOriginalFormData(editFormData);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (hasFormChanged()) {
      setUnsavedChangesDialogOpen(true);
    } else {
      setDialogOpen(false);
      setEditingCategory(null);
    }
  };

  const handleConfirmClose = () => {
    setUnsavedChangesDialogOpen(false);
    setDialogOpen(false);
    setEditingCategory(null);
  };

  const handleCancelClose = () => {
    setUnsavedChangesDialogOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingCategory) {
        const updateDto: UpdateKBCategoryDto = {
          name: formData.name,
          displayOrder: formData.displayOrder,
        };
        await updateKBCategory(editingCategory.id, updateDto);
      } else {
        await createKBCategory(formData);
      }
      setDialogOpen(false);
      setEditingCategory(null);
      loadData();
    } catch (err: unknown) {
      console.error('Failed to save category:', err);
      setError((axios.isAxiosError(err) ? err.response?.data : undefined) || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (category: KBCategoryDto) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      await deleteKBCategory(categoryToDelete.id);
      handleCloseDeleteDialog();
      loadData();
    } catch (err: unknown) {
      console.error('Failed to delete category:', err);
      setError((axios.isAxiosError(err) ? err.response?.data : undefined) || 'Failed to delete category. Category must be empty.');
      handleCloseDeleteDialog();
    }
  };

  const handleFormChange = (field: keyof CreateKBCategoryDto, value: CreateKBCategoryDto[keyof CreateKBCategoryDto]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const renderCategoryTree = (categoryList: KBCategoryDto[], level: number = 0) => {
    return categoryList.map((category) => (
      <Box key={category.id} sx={{ ml: level * 4 }}>
        <Paper sx={{ mb: 1, p: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <FolderOpen color="primary" />
              <Box>
                <Typography variant="subtitle1">{category.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Level {category.level} • {category.articleCount} articles • Order: {category.displayOrder}
                </Typography>
              </Box>
            </Box>
            <Box>
              {category.level < MAX_CATEGORY_DEPTH && (
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => handleOpenCreateDialog(category)}
                  sx={{ mr: 1 }}
                >
                  Add Subcategory
                </Button>
              )}
              <IconButton
                size="small"
                onClick={() => handleOpenEditDialog(category)}
                title="Edit category"
                aria-label="Edit category"
              >
                <Edit />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleOpenDeleteDialog(category)}
                title="Delete category"
                aria-label="Delete category"
                disabled={category.childCategories.length > 0 || category.articleCount > 0}
              >
                <Delete />
              </IconButton>
            </Box>
          </Box>
        </Paper>
        {category.childCategories.length > 0 && renderCategoryTree(category.childCategories, level + 1)}
      </Box>
    ));
  };

  const getAllCategoriesFlat = (categoryList: KBCategoryDto[]): KBCategoryDto[] => {
    let result: KBCategoryDto[] = [];
    categoryList.forEach(cat => {
      result.push(cat);
      if (cat.childCategories.length > 0) {
        result = result.concat(getAllCategoriesFlat(cat.childCategories));
      }
    });
    return result;
  };

  const allCategories = getAllCategoriesFlat(categories);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Categories</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenCreateDialog()}
        >
          Create Root Category
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Categories Tree */}
      <Box>
        {categories.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No categories found. Create your first category to organize content.
            </Typography>
          </Paper>
        ) : (
          renderCategoryTree(categories)
        )}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              required
              fullWidth
            />

            {!editingCategory && (
              <FormControl fullWidth>
                <InputLabel>Parent Category</InputLabel>
                <Select
                  value={formData.parentCategoryId?.toString() || ''}
                  onChange={(e) => handleFormChange('parentCategoryId', e.target.value ? parseInt(e.target.value) : null)}
                >
                  <MenuItem value="">
                    <em>None (Root Category)</em>
                  </MenuItem>
                  {allCategories
                    .filter(cat => cat.level < MAX_CATEGORY_DEPTH) // Can only add children to levels below max depth
                    .map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {'  '.repeat(cat.level - 1)} {cat.name} (Level {cat.level})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => handleFormChange('displayOrder', parseInt(e.target.value) || 0)}
              fullWidth
              helperText="Lower numbers appear first"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !formData.name}>
            {saving ? <CircularProgress size={20} /> : (editingCategory ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{categoryToDelete?.name}&quot;?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Categories must be empty (no subcategories or articles) before deletion.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog open={unsavedChangesDialogOpen} onClose={handleCancelClose}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to close without saving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose}>Continue Editing</Button>
          <Button onClick={handleConfirmClose} color="error" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManager;
